import { useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { 
  Search, 
  SlidersHorizontal, 
  Map as MapIcon, 
  List, 
  ArrowUpDown,
  MapPin,
  Filter,
  X
} from 'lucide-react';
import { useVenuesStore } from '@/features/venues/store/useVenuesStore';
import { BottomNav } from '@/components/navigation/index.js';
import { Stack, Text, Heading } from '@/components/ui/index.js';
import { VenueCard } from './components/VenueCard/index.js';
import { MapView } from './components/MapView/index.js';
import { FilterPanel } from './components/FilterPanel/index.js';
import styles from './VenuesPage.module.css';

/**
 * VenuesPage - Venue Explorer
 * 
 * Venue listing with map/list toggle, venue cards with ratings
 * and amenities, distance indicators, and filter options.
 * 
 * Features:
 * - Toggle between Map and List views
 * - Search venues by name
 * - Filter by sport type, amenities, distance, rating, open status
 * - Sort by distance, rating, or name
 * - Glassmorphic cards with neon accents
 * - Mobile-first responsive design
 */
export function VenuesPage() {
  const {
    viewMode,
    setViewMode,
    sortBy,
    setSortBy,
    searchQuery,
    setSearchQuery,
    isFilterPanelOpen,
    toggleFilterPanel,
    closeFilterPanel,
    filters,
    clearFilters,
    getFilteredVenues,
  } = useVenuesStore();

  const [showSortDropdown, setShowSortDropdown] = useState(false);

  const filteredVenues = getFilteredVenues();

  const handleSearchChange = useCallback((e) => {
    setSearchQuery(e.target.value);
  }, [setSearchQuery]);

  const handleSortSelect = useCallback((sortOption) => {
    setSortBy(sortOption);
    setShowSortDropdown(false);
  }, [setSortBy]);

  const hasActiveFilters = 
    filters.sportTypes.length > 0 ||
    filters.amenities.length > 0 ||
    filters.maxDistance !== 10 ||
    filters.openNow ||
    filters.minRating > 0;

  const activeFilterCount = 
    filters.sportTypes.length + 
    filters.amenities.length + 
    (filters.maxDistance !== 10 ? 1 : 0) + 
    (filters.openNow ? 1 : 0) + 
    (filters.minRating > 0 ? 1 : 0);

  return (
    <div className={styles.page}>
      {/* Header Section */}
      <header className={styles.header}>
        <div className={styles.headerContent}>
          <Stack direction="row" gap={3} align="center" justify="space-between" className={styles.headerTop}>
            <Heading level={1} size="lg" color="primary">Venues</Heading>
            
            {/* View Toggle */}
            <div className={styles.viewToggle}>
              <button
                className={`${styles.viewButton} ${viewMode === 'list' ? styles.viewButtonActive : ''}`}
                onClick={() => setViewMode('list')}
                aria-label="List view"
              >
                <List size={20} />
              </button>
              <button
                className={`${styles.viewButton} ${viewMode === 'map' ? styles.viewButtonActive : ''}`}
                onClick={() => setViewMode('map')}
                aria-label="Map view"
              >
                <MapIcon size={20} />
              </button>
            </div>
          </Stack>

          {/* Search Bar */}
          <div className={styles.searchBar}>
            <MapPin size={20} className={styles.searchIcon} />
            <input
              type="text"
              placeholder="Search venues..."
              value={searchQuery}
              onChange={handleSearchChange}
              className={styles.searchInput}
            />
            {searchQuery && (
              <button 
                className={styles.clearSearch}
                onClick={() => setSearchQuery('')}
                aria-label="Clear search"
              >
                <X size={18} />
              </button>
            )}
          </div>

          {/* Filter & Sort Bar */}
          <Stack direction="row" gap={2} className={styles.filterBar}>
            {/* Filter Button */}
            <button 
              className={`${styles.filterButton} ${hasActiveFilters ? styles.filterButtonActive : ''}`}
              onClick={toggleFilterPanel}
            >
              <SlidersHorizontal size={18} />
              <span>Filters</span>
              {activeFilterCount > 0 && (
                <span className={styles.filterBadge}>{activeFilterCount}</span>
              )}
            </button>

            {/* Sort Dropdown */}
            <div className={styles.sortWrapper}>
              <button 
                className={styles.sortButton}
                onClick={() => setShowSortDropdown(!showSortDropdown)}
              >
                <ArrowUpDown size={18} />
                <span>Sort: {sortBy === 'distance' ? 'Distance' : sortBy === 'rating' ? 'Rating' : 'Name'}</span>
              </button>
              
              {showSortDropdown && (
                <>
                  <div 
                    className={styles.sortOverlay}
                    onClick={() => setShowSortDropdown(false)}
                  />
                  <div className={styles.sortDropdown}>
                    <button 
                      className={`${styles.sortOption} ${sortBy === 'distance' ? styles.sortOptionActive : ''}`}
                      onClick={() => handleSortSelect('distance')}
                    >
                      Distance
                    </button>
                    <button 
                      className={`${styles.sortOption} ${sortBy === 'rating' ? styles.sortOptionActive : ''}`}
                      onClick={() => handleSortSelect('rating')}
                    >
                      Rating
                    </button>
                    <button 
                      className={`${styles.sortOption} ${sortBy === 'name' ? styles.sortOptionActive : ''}`}
                      onClick={() => handleSortSelect('name')}
                    >
                      Name
                    </button>
                  </div>
                </>
              )}
            </div>

            {/* Results Count (only in list view) */}
            {viewMode === 'list' && (
              <Text size="sm" color="muted" className={styles.resultsCount}>
                {filteredVenues.length} {filteredVenues.length === 1 ? 'venue' : 'venues'}
              </Text>
            )}
          </Stack>

          {/* Active Filter Chips */}
          {hasActiveFilters && (
            <div className={styles.activeFilters}>
              {filters.sportTypes.map(sport => (
                <span key={sport} className={styles.filterChip}>
                  {sport}
                  <button 
                    onClick={() => useVenuesStore.getState().toggleSportType(sport)}
                    aria-label={`Remove ${sport} filter`}
                  >
                    <X size={14} />
                  </button>
                </span>
              ))}
              {filters.openNow && (
                <span className={styles.filterChip}>
                  Open Now
                  <button 
                    onClick={() => useVenuesStore.getState().toggleOpenNow()}
                    aria-label="Remove open now filter"
                  >
                    <X size={14} />
                  </button>
                </span>
              )}
              {filters.minRating > 0 && (
                <span className={styles.filterChip}>
                  {filters.minRating}+ ★
                  <button 
                    onClick={() => useVenuesStore.getState().setMinRating(0)}
                    aria-label="Remove rating filter"
                  >
                    <X size={14} />
                  </button>
                </span>
              )}
              <button 
                className={styles.clearFiltersLink}
                onClick={clearFilters}
              >
                Clear all
              </button>
            </div>
          )}
        </div>
      </header>

      {/* Main Content */}
      <main className={styles.main}>
        {viewMode === 'map' ? (
          /* Map View */
          <MapView />
        ) : (
          /* List View */
          <div className={styles.listContainer}>
            {filteredVenues.length > 0 ? (
              <Stack direction="column" gap={4} className={styles.venueList}>
                {filteredVenues.map(venue => (
                  <Link 
                    key={venue.id} 
                    to={`/venues/${venue.id}`}
                    className={styles.venueLink}
                  >
                    <VenueCard venue={venue} />
                  </Link>
                ))}
              </Stack>
            ) : (
              /* Empty State */
              <div className={styles.emptyState}>
                <div className={styles.emptyIcon}>
                  <Filter size={48} />
                </div>
                <Heading level={3} size="md" color="primary" className={styles.emptyTitle}>
                  No venues found
                </Heading>
                <Text size="base" color="secondary" align="center" className={styles.emptyText}>
                  Try adjusting your filters or search to find more venues.
                </Text>
                <button 
                  className={styles.adjustFiltersButton}
                  onClick={() => {
                    clearFilters();
                    setSearchQuery('');
                  }}
                >
                  Adjust Filters
                </button>
              </div>
            )}
          </div>
        )}
      </main>

      {/* Filter Panel */}
      <FilterPanel 
        isOpen={isFilterPanelOpen} 
        onClose={closeFilterPanel} 
      />

      {/* Bottom Navigation */}
      <BottomNav />
    </div>
  );
}

export default VenuesPage;
